import React from 'react';
import UseCasePageTemplate from '../../../components/UseCasePageTemplate';
import DataLeaderAnimation from '../../../components/DataLeaderAnimation';

const DataLeaderPage = () => {
  // Leader icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  // Icons for solutions
  const visibilityIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  const governanceIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const costIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const challenges = [
    {
      title: "Data Quality Issues Impact Business Decisions",
      description: "As a Data Leader, you face challenges when poor data quality undermines business decisions, erodes trust in data, and damages organizational credibility."
    },
    {
      title: "Limited Visibility into Data Ecosystem",
      description: "Without comprehensive observability, it's difficult to understand the full scope of your data landscape, identify bottlenecks, or spot potential issues before they cascade."
    },
    {
      title: "Resource Allocation and ROI Justification",
      description: "Determining where to allocate limited resources and justifying investments in data quality improvement initiatives requires clear metrics and measurable impact."
    },
    {
      title: "Scaling Data Governance",
      description: "As data volumes and complexity grow, maintaining consistent governance practices across the organization becomes increasingly challenging."
    }
  ];

  const solutions = [
    {
      icon: visibilityIcon,
      title: "Comprehensive Data Observability",
      description: "Qupid provides a unified view of your entire data ecosystem, giving you the visibility needed to make informed strategic decisions.",
      items: [
        "Executive dashboards with key data quality metrics",
        "System-wide health scores and trend analysis",
        "Automated discovery of critical data assets",
        "Real-time visibility into data quality incidents",
        "Cross-system lineage mapping and impact analysis"
      ]
    },
    {
      icon: governanceIcon,
      title: "Scalable Data Governance",
      description: "Implement and scale data governance practices that align with your organizational goals and compliance requirements.",
      items: [
        "Centralized policy management and enforcement",
        "Automated documentation and metadata management",
        "Role-based access controls and responsibility assignment",
        "Compliance monitoring and reporting",
        "Audit trails for data quality changes and incidents"
      ]
    },
    {
      icon: costIcon,
      title: "Resource Optimization and ROI",
      description: "Quantify the impact of data quality initiatives and optimize resource allocation based on business value.",
      items: [
        "Data quality ROI measurement frameworks",
        "Resource utilization analytics",
        "Cost of poor data quality assessments",
        "Prioritization tools for quality improvement initiatives",
        "Business impact analysis of data quality issues"
      ]
    }
  ];

  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: "Enhanced Decision-Making Confidence",
      description: "Build organizational trust in data with comprehensive quality assurance that ensures decisions are based on reliable information."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Accelerated Strategic Initiatives",
      description: "Remove data quality bottlenecks that slow down strategic initiatives and digital transformation projects."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Optimized Data Infrastructure Costs",
      description: "Identify opportunities to optimize data infrastructure and reduce the costs associated with poor data quality."
    }
  ];

  const testimonial = {
    quote: "Qupid has transformed how we manage our data ecosystem. As a Chief Data Officer, I now have complete visibility into our data quality landscape, allowing me to make strategic decisions with confidence and demonstrate clear ROI to our executive team.",
    author: "Jane Doe",
    title: "Chief Data Officer",
    company: "Enterprise Corp"
  };

  const relatedUsesCases = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: "For Data Engineers",
      description: "See how Qupid helps Data Engineers implement and maintain reliable data pipelines.",
      link: "/solutions/by-persona/data-engineer"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "For Data Users",
      description: "Discover how Qupid empowers Data Users with trusted, high-quality data.",
      link: "/solutions/by-persona/data-user"
    }
  ];

  return (
    <UseCasePageTemplate
      title="For Data Leaders"
      description="Gain comprehensive visibility into your data ecosystem, optimize data quality investments, and drive strategic value from your data assets."
      heroSvg={<DataLeaderAnimation />}
      icon={icon}
      challenges={challenges}
      solutions={solutions}
      benefits={benefits}
      testimonial={testimonial}
      relatedUsesCases={relatedUsesCases}
    />
  );
};

export default DataLeaderPage; 